import { createSystem, defaultConfig, defineConfig, defineRecipe } from '@chakra-ui/react'

/**
 * KUNTUR — "Cordillera viva".
 *
 * La firma es TERRACOTA/OCRE (botones primarios, enlaces, foco) con TEAL PROFUNDO como
 * su contraparte "verde" (riel, login, éxito), sobre crema cálido y carbón. Gama tomada
 * de las referencias del cliente (Namibia: terracota/crema/carbón; Serengeti: teal). Cambio pedido el 2026-10-17: quienes ya vieron
 * Kuntur en verde tenían que notar a primera vista que es otro sistema. Además
 * suma una PALETA DE MINERALES ANDINOS y cada mineral significa algo. Los módulos tienen su mineral (ver `SECTION_ACCENTS`) y lo
 * usan para orientarse: el riel, el encabezado de página y la barra de actividad
 * toman el color del módulo en el que estás.
 *
 * Regla: el color nunca es decorativo suelto. Si algo es lapislázuli es porque es
 * Inteligencia o es informativo; si es oro, es Logística o pide atención.
 */
export const mineral = {
  // Ocre — la firma (tierra oxidada de los Andes)
  ocre: '#cf6a35',
  ocreLight: '#f0b98f',
  ocreDeep: '#b4532a',
  ocreDark: '#8c3d1d',
  ocreSoft: '#f9e6da',

  // Bosque — la noche de la marca
  // Tierra ocre — el riel lateral
  tierra950: '#22150b',
  tierra900: '#331f10',
  tierra800: '#472b16',
  tierra700: '#5e3a1c',

  bosque950: '#0a2324',
  bosque900: '#0f2f30',
  bosque800: '#164041',
  bosque700: '#1d5354',
  bosque600: '#22706f',
  bosque500: '#2a8a87',
  bosque300: '#93cfc9',
  bosque100: '#dcefed',

  oro: '#d49a2a',
  oroDeep: '#a8740e',
  oroSoft: '#f9eccf',

  cobre: '#a0683a',
  cobreDeep: '#7c4e28',
  cobreSoft: '#f1e3d5',

  lapis: '#3d6fa8',
  lapisDeep: '#2b5285',
  lapisSoft: '#e2eaf4',

  turquesa: '#2a8a87',
  turquesaDeep: '#1d6664',
  turquesaSoft: '#dcefed',

  rodocrosita: '#c0577a',
  rodocrositaDeep: '#97405c',
  rodocrositaSoft: '#f7e2e9',

  amatista: '#7a5a9e',
  amatistaDeep: '#5c4279',
  amatistaSoft: '#ece5f4',

  sabana: '#5f8f3e',
  sabanaDeep: '#46702b',
  sabanaSoft: '#e7f0dc',

  pizarra: '#4a4744',
  pizarraSoft: '#e9e6e2',

  aji: '#c8442f',
  ajiSoft: '#fae1db',

  // Superficies cálidas (papel de altura, no blanco clínico)
  canvas: '#ffffff',
  surface: '#ffffff',
  surfaceAlt: '#faf9f7',
  sunk: '#f3f1ed',
  line: '#e6e2db',
  lineStrong: '#cfc8bd',

  ink: '#2a2622',
  ink2: '#4f4842',
  ink3: '#857b71',
  ink4: '#b5aa9d',
} as const

/** Mineral de cada módulo (por el `code` raíz del menú). */
export const SECTION_ACCENTS: Record<string, { color: string; deep: string; soft: string; name: string }> = {
  home: { color: mineral.ocre, deep: mineral.ocreDeep, soft: mineral.ocreSoft, name: 'Ocre' },
  intelligence: { color: mineral.lapis, deep: mineral.lapisDeep, soft: mineral.lapisSoft, name: 'Lapislázuli' },
  identity: { color: mineral.rodocrosita, deep: mineral.rodocrositaDeep, soft: mineral.rodocrositaSoft, name: 'Rodocrosita' },
  origin: { color: mineral.cobre, deep: mineral.cobreDeep, soft: mineral.cobreSoft, name: 'Cobre' },
  logistics: { color: mineral.oro, deep: mineral.oroDeep, soft: mineral.oroSoft, name: 'Oro' },
  plant: { color: mineral.turquesa, deep: mineral.turquesaDeep, soft: mineral.turquesaSoft, name: 'Turquesa' },
  closeout: { color: mineral.sabana, deep: mineral.sabanaDeep, soft: mineral.sabanaSoft, name: 'Sabana' },
  analytics: { color: mineral.amatista, deep: mineral.amatistaDeep, soft: mineral.amatistaSoft, name: 'Amatista' },
  security: { color: mineral.pizarra, deep: '#2f2c2a', soft: mineral.pizarraSoft, name: 'Carbón' },
  masters: { color: '#a08a6c', deep: '#7a6750', soft: '#f1ebe1', name: 'Arena' },
}

export const accentFor = (sectionCode?: string | null) =>
  SECTION_ACCENTS[(sectionCode ?? '').split('.')[0] ?? ''] ?? SECTION_ACCENTS.home!

export const fonts = {
  display: "'Bricolage Grotesque', 'Instrument Sans', sans-serif",
  body: "'Instrument Sans', system-ui, sans-serif",
  mono: "'JetBrains Mono', ui-monospace, monospace",
}

/**
 * Tokens heredados (Material 3). Las páginas los usan por nombre; aquí sólo cambian
 * los VALORES, para que todo el sistema adopte la nueva paleta sin tocar cada pantalla.
 */
export const m3 = {
  primary: mineral.ocreDark,
  primaryContainer: mineral.ocreDeep,
  onPrimary: '#ffffff',
  onPrimaryContainer: mineral.ocreLight,

  secondary: mineral.pizarra,
  secondaryContainer: mineral.bosque100,
  onSecondary: '#ffffff',

  background: mineral.canvas,
  surface: mineral.canvas,
  surfaceContainerLowest: mineral.surface,
  surfaceContainerLow: mineral.surfaceAlt,
  surfaceContainer: mineral.sunk,
  surfaceContainerHigh: '#ebe8e2',
  surfaceContainerHighest: '#e4dfd3',
  surfaceDim: '#d9d3c5',

  onBackground: mineral.ink,
  onSurface: mineral.ink,
  onSurfaceVariant: mineral.ink2,

  outline: mineral.ink3,
  outlineVariant: mineral.line,

  error: mineral.aji,
  errorContainer: mineral.ajiSoft,
  onErrorContainer: '#8a1f10',
  success: mineral.bosque500,
  warning: mineral.oro,
  info: mineral.lapis,

  inverseSurface: mineral.bosque900,
  inverseOnSurface: '#eef1ef',
  inversePrimary: mineral.ocreLight,
} as const

// ── Recipes ──

const inputRecipe = defineRecipe({
  base: {
    borderRadius: '10px',
    color: m3.onSurface,
    _placeholder: { color: mineral.ink4 },
    focusRingColor: mineral.ocre,
    transition: 'box-shadow .18s ease, background .18s ease, border-color .18s ease',
    _disabled: { opacity: 0.7, cursor: 'not-allowed' },
  },
  variants: {
    variant: {
      outline: {
        bg: mineral.surfaceAlt,
        borderWidth: '1px',
        borderColor: mineral.line,
        _hover: { borderColor: mineral.lineStrong },
        _focusVisible: { bg: mineral.surface, borderColor: mineral.ocre, boxShadow: `0 0 0 4px ${mineral.ocreSoft}` },
        focusVisibleRing: 'none',
      },
      subtle: {
        bg: mineral.sunk,
        _focusVisible: { bg: mineral.surface, boxShadow: `0 0 0 4px ${mineral.ocreSoft}` },
        focusVisibleRing: 'none',
      },
    },
  },
})

const textareaRecipe = defineRecipe({
  base: {
    bg: mineral.surfaceAlt,
    borderWidth: '1px',
    borderColor: mineral.line,
    borderRadius: '10px',
    color: m3.onSurface,
    _placeholder: { color: mineral.ink4 },
    _focusVisible: { bg: mineral.surface, borderColor: mineral.ocre, boxShadow: `0 0 0 4px ${mineral.ocreSoft}` },
    focusVisibleRing: 'none',
  },
})

const config = defineConfig({
  globalCss: {
    body: { bg: mineral.canvas, color: mineral.ink, fontFamily: fonts.body },
    '::selection': { bg: mineral.ocreLight, color: mineral.bosque950 },
  },
  theme: {
    recipes: {
      input: inputRecipe,
      textarea: textareaRecipe,
    },
    // 2xl a 1440px (antes lo usaba el header para alternar icon-only → texto).
    breakpoints: {
      '2xl': '1440px',
    },
    tokens: {
      fonts: {
        heading: { value: fonts.display },
        body: { value: fonts.body },
        mono: { value: fonts.mono },
      },
      colors: {
        green: {
          50: { value: '#eefaf3' },
          100: { value: mineral.bosque100 },
          200: { value: '#b7e6cb' },
          300: { value: mineral.bosque300 },
          400: { value: '#4fb382' },
          500: { value: mineral.bosque500 },
          600: { value: mineral.bosque600 },
          700: { value: mineral.bosque700 },
          800: { value: mineral.bosque800 },
          900: { value: mineral.bosque900 },
          950: { value: mineral.bosque950 },
        },
        gold: {
          50: { value: '#fffaf0' },
          100: { value: mineral.oroSoft },
          200: { value: '#f9dd9c' },
          300: { value: '#f4c65b' },
          400: { value: '#efb236' },
          500: { value: mineral.oro },
          600: { value: mineral.oroDeep },
          700: { value: '#8f5d05' },
          800: { value: '#6b4504' },
          900: { value: '#4a3003' },
          950: { value: '#2b1c02' },
        },
        red: { 500: { value: mineral.aji }, 600: { value: '#b83321' } },
        blue: { 500: { value: mineral.lapis }, 600: { value: mineral.lapisDeep } },
      },
    },
  },
})

export const system = createSystem(defaultConfig, config)

// ── Objetos exportados (todos referencian la paleta) ──

export const brand = {
  /** Botón primario: ocre de marca, plano (sin degradado). */
  primary: mineral.ocreDeep,
  /**
   * Acento de marca para enlaces, activos y focos. Conserva el nombre `green` porque
   * lo usan ~300 sitios; desde el cambio de marca su valor es OCRE.
   */
  green: mineral.ocreDeep,
  greenLight: mineral.ocre,
  greenDark: mineral.ocreDark,
  gold: mineral.oro,
  goldLight: '#f4c65b',
  goldDark: mineral.oroDeep,
} as const

export const themeColors = {
  bg: mineral.canvas,
  bgPanel: mineral.surface,
  bgCard: mineral.surfaceAlt,
  bgCardSolid: mineral.surfaceAlt,
  bgHover: mineral.sunk,
  bgActive: 'rgba(207, 106, 53, 0.10)',
  border: mineral.line,
  borderHover: mineral.lineStrong,
  text: mineral.ink,
  textSecondary: mineral.ink2,
  textMuted: mineral.ink3,
  textDimmed: mineral.ink4,
  sidebar: mineral.bosque900,
  header: mineral.canvas,
} as const

export const statusColors = {
  success: mineral.bosque500,
  warning: mineral.oro,
  error: mineral.aji,
  info: mineral.lapis,
} as const

export const colors = {
  primary: mineral.ocreDeep,
  primaryLight: mineral.ocre,
  primaryDark: mineral.ocreDark,
  secondary: mineral.oro,
  secondaryLight: '#f4c65b',
  secondaryDark: mineral.oroDeep,
  light: mineral.surface,
  lightDark: mineral.line,
  gray: mineral.ink3,
  grayLight: mineral.line,
  success: mineral.bosque500,
  warning: mineral.oro,
  error: mineral.aji,
} as const

/** Sombras con tinte tierra en vez de gris neutro: tarjetas en relieve suave, como las referencias. */
export const shadows = {
  sm: '0 1px 2px rgba(74, 52, 30, 0.07), 0 2px 6px -2px rgba(74, 52, 30, 0.06)',
  md: '0 12px 30px -16px rgba(74, 52, 30, 0.28), 0 1px 2px rgba(74, 52, 30, 0.06)',
  lg: '0 26px 50px -18px rgba(60, 40, 22, 0.35), 0 4px 12px rgba(60, 40, 22, 0.08)',
  glow: (c: string) => `0 0 0 4px ${c}22, 0 8px 22px -8px ${c}88`,
}
